import Axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Col, Form, InputGroup, Row } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import Modal from "react-bootstrap/Modal";
import { FaEye } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import departmentAvatar from "../assets/images/user/department-avatar.png";
import useStoredPermissions from "./useStoredPermissions";
import { DataTablePagination } from "./utils/dataTablePagination";
import { SideBar } from "./sideBar";
import { NavBar } from "./navBar";
import { DropDownClients } from "./clients/dropDownClients";
import ClipLoader from "react-spinners/ClipLoader";
import {
  getApiResponseMessage,
  getApiErrorMessage,
} from "../utils/apiMessageHelper";
// Contexts
import { useNotification } from "../contexts/NotificationContext";
import { useAuth } from "../contexts/AuthContext";

export const MainDepartments = () => {
  const { t } = useTranslation();
  const permissions = useStoredPermissions() || [];
  const { clientId } = useAuth();
  const { notify } = useNotification();

  // Check if user is Super Admin (clientId === 1)
  const isSuperAdmin = clientId === 1;

  // Show sidebar if user has clientId (both Super Admin and Client Admin)
  const hasPermissions = clientId !== null && clientId !== undefined;

  //> States
  const [data, setData] = useState([]);
  const [departmentRows, setDepartmentRows] = useState([]);
  const [currentDepartment, setCurrentDepartment] = useState([]);
  const [newDepartment, setNewDepartment] = useState([]);
  const [DepartmentValidated, setDepartmentValidated] = useState(false);
  const [isDepartmentEditMode, setIsDepartmentEditMode] = useState(false);
  const [DepartmentActiveSwitch, setDepartmentActiveSwitch] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState(
    isSuperAdmin ? null : clientId,
  );
  const [isLoading, setIsLoading] = useState(false);

  //> Modals
  const [showNewDepartment, setShowNewDepartment] = useState(false);
  const [showCurrentDepartment, setShowCurrentDepartment] = useState(false);
  const [showDeleteDepartmentConfirm, setShowDeleteDepartmentConfirm] =
    useState(false);

  const handleCloseNewDepartment = () => {
    setShowNewDepartment(false);
    setNewDepartment(null);
    setDepartmentValidated(false);
  };

  const handleShowNewDepartment = () => setShowNewDepartment(true);

  const handleCloseCurrentDepartment = () => {
    setShowCurrentDepartment(false);
    setIsDepartmentEditMode(false);
  };

  const handleShowCurrentDepartment = () => setShowCurrentDepartment(true);

  const handleCloseDeleteDepartmentConfirm = () =>
    setShowDeleteDepartmentConfirm(false);

  const handleShowDeleteDepartmentConfirm = () =>
    setShowDeleteDepartmentConfirm(true);

  const handleClientChange = (selected) => {
    if (selected) {
      const newClientId = selected.idClient;
      // Allow setting to null when empty option is selected
      setSelectedClientId(newClientId);
      // Clear departments when empty option is selected
      if (newClientId === null) {
        setDepartmentRows([]);
      }
    }
  };

  //> Columns
  const departmentColumns = [
    {
      Header: " ",
      columns: [
        { Header: "ID", accessor: "idDepartment" },
        { Header: t("departments.tableHeaderName"), accessor: "name" },
        {
          Header: t("departments.tableHeaderDetail"),
          accessor: "detalle",
          Cell: (props) => {
            return (
              <FaEye
                className="fa-table-icon"
                onClick={() => viewCurrentDepartment(props.cell.row.original)}
              />
            );
          },
        },
      ],
    },
  ];

  //> API Calls
  const getDepartments = useCallback(async () => {
    if (!selectedClientId) return;

    setIsLoading(true);
    try {
      const res = await Axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/Department/list/${selectedClientId}`,
      );

      if (res.data.length === 0) {
        setData((prev) => ({
          ...prev,
          message: t("departments.noDepartmentsMessage"),
        }));
      }

      setDepartmentRows(res.data.responseObject);
    } catch (e) {
      setData((prev) => ({
        ...prev,
        message: e.message,
      }));
    } finally {
      setIsLoading(false);
    }
  }, [selectedClientId]);

  async function deleteDepartment() {
    try {
      const res = await Axios.delete(
        `${process.env.REACT_APP_API_URL}/api/v1/Department/${currentDepartment.idDepartment}`,
      );
      if (res.status === 200) {
        const message =
          getApiResponseMessage(res) ||
          t("departments.departmentDeletedSuccess");
        notify(message, { type: "success" });
      }
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    } finally {
      setShowCurrentDepartment(false);
      getDepartments();
      setCurrentDepartment(null);
      handleCloseDeleteDepartmentConfirm();
    }
  }

  //> Handlers
  const handleNewDepartmentSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      setDepartmentValidated(true);
    } else {
      setDepartmentValidated(false);

      try {
        newDepartment.status = DepartmentActiveSwitch ? 0 : 1;
        const res = await Axios.post(
          `${process.env.REACT_APP_API_URL}/api/v1/Department/${selectedClientId}`,
          newDepartment,
        );
        if (res.status === 200) {
          const message =
            getApiResponseMessage(res) ||
            t("departments.departmentAddedSuccess");
          notify(message, { type: "success" });
        }
      } catch (e) {
        const errorMessage =
          getApiErrorMessage(e) || t("departments.departmentAddError");
        notify(errorMessage, { type: "error" });
      } finally {
        setShowNewDepartment(false);
        getDepartments();
        setNewDepartment(null);
      }
    }
  };

  const handleEditDepartmentSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      setDepartmentValidated(true);
    } else {
      setDepartmentValidated(false);

      try {
        const res = await Axios.put(
          `${process.env.REACT_APP_API_URL}/api/v1/Department/${currentDepartment.idDepartment}`,
          currentDepartment,
        );

        if (res.status === 200) {
          const message =
            getApiResponseMessage(res) ||
            t("departments.departmentModifiedSuccess");
          notify(message, { type: "success" });
        }
      } catch (e) {
        const errorMessage =
          getApiErrorMessage(e) || t("departments.departmentModifyError");
        notify(errorMessage, { type: "error" });
      } finally {
        getDepartments();
        setCurrentDepartment(null);
        setDepartmentActiveSwitch(true);
        handleCloseCurrentDepartment();
      }
    }
  };

  const handleNewDepartmentChange = (object) => {
    const { name, value } = object.target;
    setNewDepartment((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCurrentDepartmentChange = (object) => {
    const { name, value } = object.target;
    setCurrentDepartment((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDepartmentEditMode = () => {
    setIsDepartmentEditMode(!isDepartmentEditMode);
  };

  const viewCurrentDepartment = (object) => {
    setCurrentDepartment(object);
    handleShowCurrentDepartment();
  };

  //> Lifecycle
  useEffect(() => {
    if (selectedClientId) {
      getDepartments();
    }
  }, [selectedClientId, getDepartments]);

  return (
    <React.Fragment>
      {hasPermissions && <SideBar />}
      <Container>
        <NavBar />
        <Container>
          <Row>
            <Card>
              <Card.Header>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Card.Title as="h5">{t("departments.pageTitle")}</Card.Title>
                  {isSuperAdmin && (
                    <div style={{ minWidth: "250px" }}>
                      <DropDownClients
                        value={selectedClientId}
                        onChange={handleClientChange}
                        className="form-control"
                      />
                    </div>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                <div id="flexDiv">
                  <Button
                    id="btnLarge"
                    variant="primary"
                    onClick={handleShowNewDepartment}
                    disabled={
                      !selectedClientId ||
                      !permissions?.includes("Add/Update Department")
                    }
                  >
                    {t("common.add")}
                  </Button>
                </div>
                <Col xs={12}>
                  {isLoading ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "200px",
                      }}
                    >
                      <ClipLoader color="#007bff" size={50} />
                    </div>
                  ) : departmentRows.length === 0 ? (
                    <div className="no-data">{data?.message}</div>
                  ) : (
                    <DataTablePagination
                      columns={departmentColumns}
                      data={departmentRows}
                      onRowClick={(selectedRow) =>
                        setCurrentDepartment(selectedRow)
                      }
                      hiddenColumns={["idDepartment"]}
                    />
                  )}
                </Col>
              </Card.Body>
            </Card>
          </Row>
        </Container>
      </Container>

      {/* Modal: Create New Department */}
      <Modal show={showNewDepartment} onHide={handleCloseNewDepartment}>
        <Form
          className="addForm"
          onSubmit={handleNewDepartmentSubmit}
          noValidate
          validated={DepartmentValidated}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {t("departments.createDepartmentModalTitle")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="justify-content-md-center">
              <h5 className="steps-color">
                {t("departments.departmentNameStep")}
              </h5>
              {/* Name */}
              <InputGroup className="mb-3">
                <InputGroup.Text>{t("departments.nameLabel")}</InputGroup.Text>
                <Form.Control
                  required
                  minLength={3}
                  autoFocus
                  type="text"
                  name="name"
                  value={newDepartment?.name || ""}
                  placeholder={t("departments.nameLabel")}
                  onChange={handleNewDepartmentChange}
                />
              </InputGroup>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseNewDepartment}>
              {t("common.close")}
            </Button>
            <Button variant="primary" type="submit">
              {t("common.save")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: View/Edit Current Department */}
      <Modal
        className="modal"
        show={showCurrentDepartment}
        onHide={handleCloseCurrentDepartment}
      >
        <Form
          className="addForm"
          onSubmit={handleEditDepartmentSubmit}
          noValidate
          validated={DepartmentValidated}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {t("departments.viewDepartmentModalTitle")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="justify-content-md-center">
              <Card>
                <Card.Body className="p-0">
                  <div>
                    <div className="text-center project-main">
                      <img
                        className="img-fluid rounded-circle"
                        src={departmentAvatar}
                        alt="dashboard-user"
                      />
                      {/* Info View */}
                      <Row
                        style={{
                          display: !isDepartmentEditMode ? "flex" : "none",
                        }}
                      >
                        <Col xs={12}>
                          {/* ID */}
                          <div className="row m-t-30">
                            <div className="col-6">
                              <span>{t("departments.idLabel")}</span>
                            </div>
                            <div className="col-6">
                              <h6>{currentDepartment?.idDepartment}</h6>
                            </div>
                          </div>
                          {/* Name */}
                          <div className="row m-t-30">
                            <div className="col-6">
                              <span>
                                {t("departments.departmentNameLabel")}
                              </span>
                            </div>
                            <div className="col-6">
                              <h6>{currentDepartment?.name}</h6>
                            </div>
                          </div>
                        </Col>
                      </Row>
                      {/* Edit View */}
                      <Row
                        style={{
                          display: isDepartmentEditMode ? "flex" : "none",
                        }}
                      >
                        <Col xs={12}>
                          {/* Name */}
                          <InputGroup className="mb-3">
                            <InputGroup.Text>
                              {t("departments.nameLabel")}
                            </InputGroup.Text>
                            <Form.Control
                              required
                              minLength={3}
                              autoFocus
                              type="text"
                              name="name"
                              value={currentDepartment?.name || ""}
                              placeholder={t("departments.nameLabel")}
                              onChange={handleCurrentDepartmentChange}
                            />
                          </InputGroup>
                        </Col>
                      </Row>
                      <div
                        className="row m-t-30"
                        style={{
                          visibility:
                            currentDepartment?.idDepartment !== null
                              ? "visible"
                              : "hidden",
                        }}
                      >
                        <div className="col-6 p-r-0">
                          {!isDepartmentEditMode && (
                            <Link
                              onClick={handleDepartmentEditMode}
                              className="btn btn-primary  text-uppercase btn-block"
                            >
                              {t("common.edit")}
                            </Link>
                          )}
                          {isDepartmentEditMode && (
                            <Button
                              type="submit"
                              className="btn btn-primary  text-uppercase btn-block"
                            >
                              {t("common.save")}
                            </Button>
                          )}
                        </div>
                        <div className="col-6">
                          <Button
                            onClick={handleShowDeleteDepartmentConfirm}
                            className="btn text-uppercase border btn-block btn-outline-danger"
                            disabled={
                              !permissions.includes("Delete Department")
                            }
                          >
                            {t("common.delete")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseCurrentDepartment}>
              {t("common.close")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: Delete Department Confirmation */}
      <Modal
        show={showDeleteDepartmentConfirm}
        onHide={handleCloseDeleteDepartmentConfirm}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("common.confirmDeleteTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("departments.deleteDepartmentConfirmMessage")}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseDeleteDepartmentConfirm}
          >
            {t("common.cancel")}
          </Button>
          <Button variant="danger" onClick={deleteDepartment}>
            {t("common.delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};
